import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  CementProduct,
  cementProductNombrePublicacionSelectLabel,
  cementProductPriceWithIva,
} from 'src/app/models/cement-product';

const PRODUCT_FALLBACK_IMAGE = 'assets/img/product/construccion.jpg';

@Component({
  selector: 'app-product-card-select',
  templateUrl: './product-card-select.component.html',
  styleUrls: ['./product-card-select.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductCardSelectComponent),
      multi: true,
    },
  ],
})
export class ProductCardSelectComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly products = input<CementProduct[]>([]);
  readonly selectionDelayMs = input<number>(0);

  value: CementProduct | null = null;
  disabled = false;
  selecting = false;
  private selectionTimer: ReturnType<typeof setTimeout> | null = null;

  private onChange: (value: CementProduct | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.destroyRef.onDestroy(() => this.clearSelectionTimer());
  }

  writeValue(value: CementProduct | null): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: CementProduct | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.clearSelectionTimer();
      this.selecting = false;
    }
    this.cdr.markForCheck();
  }

  select(product: CementProduct): void {
    if (this.disabled || this.selecting) {
      return;
    }
    const delayMs = Number(this.selectionDelayMs() ?? 0);
    this.value = product;
    if (delayMs > 0) {
      this.selecting = true;
      this.clearSelectionTimer();
      this.selectionTimer = setTimeout(() => {
        this.selectionTimer = null;
        this.onChange(product);
        this.onTouched();
        this.selecting = false;
        this.cdr.markForCheck();
      }, delayMs);
    } else {
      this.onChange(product);
      this.onTouched();
    }
    this.cdr.markForCheck();
  }

  isLocked(): boolean {
    return this.disabled || this.selecting;
  }

  isSelected(product: CementProduct): boolean {
    return this.value?.id === product.id;
  }

  getLabel(product: CementProduct): string {
    return cementProductNombrePublicacionSelectLabel(product);
  }

  getFormattedPrice(product: CementProduct): string {
    const price = cementProductPriceWithIva(product);
    if (price <= 0) {
      return '';
    }
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }).format(price);
  }

  getImageUrl(product: CementProduct): string {
    return product.image_url ?? PRODUCT_FALLBACK_IMAGE;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PRODUCT_FALLBACK_IMAGE;
  }

  private clearSelectionTimer(): void {
    if (this.selectionTimer !== null) {
      clearTimeout(this.selectionTimer);
      this.selectionTimer = null;
    }
  }
}
